import styled from '@emotion/styled';
import { Table } from 'antd';

import { colorConstants } from 'config/colorConstants';

import {
    getTableCellHeightForKpiCount,
    TABLE_SEGMENT_COLUMN_WIDTH_PX,
    TABLE_VENDOR_COLUMN_WIDTH_PX,
} from './packArchitectureTable.helpers';
import type { TableRow } from './packArchitectureTable.helpers';
import { packArchitectureTableHoverStyles } from './packArchitectureTableHover.styles';

export { CustomCheckbox } from './PackArchitectureTableCheckbox.styles';

export const TableContainer = styled.div`
    margin-top: 16px;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid ${colorConstants.greyAlt};
    background: ${colorConstants.white};
`;

export const TableToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    height: 44px;
    padding: 0 16px;
    background: ${colorConstants.greyAlt9};

    .toolbar-left {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
        min-width: 0;
    }

    .toolbar-title {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
    }

    .toolbar-title-text {
        margin: 0;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 18px;
        color: ${colorConstants.blackTitle};
    }

    .toolbar-title-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        line-height: 1;
        color: ${colorConstants.greyAlt14};
        cursor: pointer;
    }

    .toolbar-checkboxes {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;

        .ant-checkbox-wrapper {
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
            color: ${colorConstants.blackTransparent88};
        }
    }

    .toolbar-search {
        display: flex;
        justify-content: flex-end;
        flex-shrink: 0;
    }
`;

export const TableSearchGroup = styled.div`
    display: inline-flex;
    align-items: stretch;
    width: 204px;
    height: 32px;
    border-radius: 6px;
    border: 1px solid ${colorConstants.greyAlt};
    background: ${colorConstants.white};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
        border-color: ${colorConstants.violet};
        box-shadow: 0 0 0 2px ${colorConstants.controlOutline};

        button {
            border-left-color: ${colorConstants.violet};

            .anticon {
                color: ${colorConstants.violet};
            }
        }
    }

    .table-search-input.ant-input-affix-wrapper,
    .table-search-input.ant-input {
        flex: 1;
        min-width: 0;
        height: 100%;
        border: none;
        box-shadow: none;
        border-radius: 6px 0 0 6px;
        padding-left: 10px;
        background: transparent;

        &:hover,
        &:focus,
        &.ant-input-affix-wrapper-focused {
            border: none;
            box-shadow: none;
        }

        .ant-input {
            font-size: 14px;
            line-height: 18px;
            background: transparent;
        }
    }
`;

export const TableSearchButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    border: 0;
    border-left: 1px solid ${colorConstants.greyAlt};
    border-radius: 0 6px 6px 0;
    background: ${colorConstants.white};
    padding: 0;
    cursor: pointer;
    transition: border-color 0.15s ease;

    .anticon {
        font-size: 14px;
        line-height: 1;
        color: ${colorConstants.greyAlt14};
        transition: color 0.15s ease;
    }
`;
export const TableContent = styled.div`
    position: relative;

    .ant-table-wrapper,
    .ant-table,
    .ant-table-container,
    .ant-table-header,
    .ant-table-content,
    .ant-table-content > table,
    .ant-table-header > table,
    .ant-table-thead,
    .ant-table-thead > tr > th {
        border-top: 0 !important;
    }

    .ant-table-header {
        box-shadow: none !important;
    }

    .ant-table.ant-table-bordered > .ant-table-container {
        border-top: 0 !important;
        border-left: 0 !important;
    }

    .ant-table.ant-table-bordered > .ant-table-container > .ant-table-header > table,
    .ant-table.ant-table-bordered > .ant-table-container > .ant-table-content > table {
        border-top: 0 !important;
    }

    .ant-table-thead > tr > th::before {
        display: none !important;
    }
`;

export const TableLoaderOverlay = styled.div<{ $minHeight: number }>`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: ${({ $minHeight }) => $minHeight}px;
    background: ${colorConstants.white};
`;

export const TableEmptyState = styled.div<{ $minHeight: number }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: ${({ $minHeight }) => $minHeight}px;
    padding: 24px 16px;
    background: ${colorConstants.white};
`;

export const StyledTable = styled(Table<TableRow>)<{ $kpiTickCount: number }>`
    .ant-table {
        background: ${colorConstants.white};
    }

    .ant-table-thead > tr > th {
        background: ${colorConstants.greyAlt9} !important;
        font-weight: 600;
        font-size: 12px;
        color: ${colorConstants.blackTransparent88};
        border-top: none !important;
        border-bottom: 1px solid ${colorConstants.greyAlt2} !important;
        border-right: 1px solid ${colorConstants.greyAlt2} !important;
        border-inline-end: 1px solid ${colorConstants.greyAlt2} !important;
        box-shadow: none !important;
        height: 46px;
        padding: 0 16px !important;
        white-space: nowrap;
        text-align: left;
        vertical-align: middle;
    }

    .ant-table-thead > tr > th:first-of-type {
        text-align: left;
    }

    .ant-table-thead > tr > th.ant-table-cell-fix-left,
    .ant-table-tbody > tr > td.ant-table-cell-fix-left {
        width: ${TABLE_SEGMENT_COLUMN_WIDTH_PX}px !important;
        min-width: ${TABLE_SEGMENT_COLUMN_WIDTH_PX}px !important;
        max-width: ${TABLE_SEGMENT_COLUMN_WIDTH_PX}px !important;
    }

    .ant-table-thead > tr > th.ant-table-cell-fix-left {
        z-index: 3;
        background: ${colorConstants.greyAlt9} !important;
        border-right: 1px solid ${colorConstants.borderGrey} !important;
        border-inline-end: 1px solid ${colorConstants.borderGrey} !important;
    }

    .ant-table-tbody > tr > td.skeleton-cell {
        background: ${colorConstants.greyAlt9} !important;
    }

    .ant-table-tbody > tr:hover > td.skeleton-cell {
        background: ${colorConstants.greyAlt9} !important;
    }

    .ant-table-tbody > tr > td {
        font-size: 12px;
        height: ${({ $kpiTickCount }) => getTableCellHeightForKpiCount($kpiTickCount)}px;
        padding: 0 !important;
        border-bottom: 1px solid ${colorConstants.greyAlt2} !important;
        border-right: 1px solid ${colorConstants.greyAlt2} !important;
        border-inline-end: 1px solid ${colorConstants.greyAlt2} !important;
        vertical-align: middle;
        background: ${colorConstants.white};
    }

    .ant-table-tbody > tr:hover > td {
        background: ${colorConstants.white} !important;
    }

    ${packArchitectureTableHoverStyles}

    .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
        background: ${colorConstants.greyAlt9} !important;
    }

    .ant-table-tbody > tr > td.ant-table-cell-fix-left {
        font-weight: 600;
        font-size: 12px;
        color: ${colorConstants.blackTransparent88};
        background: ${colorConstants.greyAlt9} !important;
        border-right: 1px solid ${colorConstants.borderGrey} !important;
        border-inline-end: 1px solid ${colorConstants.borderGrey} !important;
        padding: 0 16px !important;
    }

    .ant-table-tbody > tr > td.vendor-cell,
    .ant-table-tbody > tr > td.vendor-cell-empty {
        width: ${TABLE_VENDOR_COLUMN_WIDTH_PX}px;
        min-width: ${TABLE_VENDOR_COLUMN_WIDTH_PX}px;
        max-width: ${TABLE_VENDOR_COLUMN_WIDTH_PX}px;
    }

    .ant-table-tbody > tr > td.vendor-cell,
    .ant-table-tbody > tr > td.vendor-cell-empty {
        text-align: left;
    }

    .ant-table-tbody > tr.table-row-selected > td {
        background: ${colorConstants.radioLabelPressedBg} !important;
        border-bottom: 1px solid ${colorConstants.greyAlt6} !important;
    }

    .ant-table-tbody > tr.table-row-selected > td.ant-table-cell-fix-left {
        background: ${colorConstants.tableHeaderBg} !important;
    }

    .ant-table-tbody > tr.table-row-dimmed > td {
        opacity: 0.45;
    }

    .ant-table-tbody > tr > td.segment-cell-selected {
        background: ${colorConstants.tableHeaderBg} !important;
        font-weight: 600;
    }

    .ant-table-tbody > tr > td.segment-cell-dimmed {
        opacity: 0.45;
    }

    .ant-table-tbody > tr > td.vendor-cell-row-selected {
        background: ${colorConstants.radioLabelPressedBg} !important;
        border-bottom: 1px solid ${colorConstants.greyAlt6} !important;
    }

    .ant-table-tbody > tr > td.vendor-cell-selected {
        border: 1px solid ${colorConstants.violetAlt1} !important;
        background: ${colorConstants.grey} !important;
        z-index: 1;
        position: relative;
    }

    .ant-table-tbody > tr:hover > td.vendor-cell-selected {
        background: ${colorConstants.grey} !important;
    }

    .ant-table-tbody > tr.table-row-selected:hover > td {
        background: ${colorConstants.radioLabelPressedBg} !important;
    }

    .ant-table-tbody > tr.table-row-selected:hover > td.vendor-cell-selected {
        background: ${colorConstants.grey} !important;
    }

    .ant-table-tbody > tr.table-row-selected:hover > td.ant-table-cell-fix-left {
        background: ${colorConstants.tableHeaderBg} !important;
    }

    .ant-table-tbody > tr > td.vendor-cell-dimmed {
        opacity: 0.45;
    }

    .ant-table-tbody > tr.table-row-clickable {
        cursor: pointer;
    }

    .ant-table-tbody > tr > td.vendor-cell,
    .ant-table-tbody > tr > td.vendor-cell-empty {
        cursor: pointer;
    }

    .ant-table-body {
        overflow: auto !important;
    }

    .ant-table-small .ant-table-thead > tr > th {
        padding: 0 16px !important;
    }

    .ant-table-small .ant-table-tbody > tr > td.ant-table-cell-fix-left {
        padding: 0 16px !important;
    }

    .ant-table-container {
        border-inline-start: none !important;
        border-top: none !important;
        border-left: none !important;
    }

    .ant-table-header {
        border-top: none !important;
        box-shadow: none !important;
    }

    /* Ant Design bordered + scroll sets border-top on header table — remove seam under toolbar */
    &.ant-table-wrapper .ant-table.ant-table-bordered > .ant-table-container > .ant-table-header > table,
    &.ant-table-wrapper .ant-table.ant-table-bordered > .ant-table-container > .ant-table-content > table,
    .ant-table.ant-table-bordered > .ant-table-container > .ant-table-header > table,
    .ant-table.ant-table-bordered > .ant-table-container > .ant-table-content > table {
        border-top: 0 !important;
    }
`;

export const KpiCellStackContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
`;

export const KpiCellRow = styled.div<{ $showDivider?: boolean }>`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-start;
    min-height: 0;
    width: 100%;
    padding: 0 8px;
    text-align: left;

    & > * {
        width: 100%;
    }

    ${({ $showDivider }) =>
        $showDivider ? `border-bottom: 1px solid ${colorConstants.greyAlt2};` : ''}
`;

export const TableFooter = styled.div`
    display: flex;
    align-items: center;
    height: 30px;
    padding: 0 16px;
    background: ${colorConstants.greyAlt6};
    border-top: 1px solid ${colorConstants.greyAlt2};
    font-size: 12px;
    color: ${colorConstants.axisLabelGrey};
`;

type SkeletonBarVariant = 'primary' | 'secondary' | 'segment' | 'header';

export const SkeletonBar = styled.span<{ $variant?: SkeletonBarVariant }>`
    display: block;
    border-radius: 4px;
    background: ${colorConstants.greyAlt};
    animation: pack-arch-table-skeleton-pulse 1.4s ease-in-out infinite;

    ${({ $variant }) => {
        if ($variant === 'segment') {
            return `
                width: 72px;
                height: 12px;
            `;
        }
        if ($variant === 'header') {
            return `
                width: 64px;
                height: 12px;
            `;
        }
        if ($variant === 'primary') {
            return `
                width: 56px;
                height: 12px;
            `;
        }
        return `
            width: 88px;
            height: 12px;
        `;
    }}

    @keyframes pack-arch-table-skeleton-pulse {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0.55;
        }
    }
`;
